import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { debounceTime, filter, finalize, map, take, takeUntil, tap } from 'rxjs/operators';
import { InputRangeComponent } from '../../input-range/input-range.component';
import { PopUpPasajeroComponent } from '../../pop-up-pasajero/pop-up-pasajero.component';
import { DistributionObjectA } from '../../pop-up-pasajero/pop-up-pasajero.model';
import { ParamsHoteles, URLHotel } from '../../tabs/tabs.models';
import { DestinyService } from '../../../../../services';
import { NotificationService } from '../../../../../services/notifications/notification.service';
import { AutocompleteDropdownComponent } from '../../../atoms';
import { AutoCompleteItem } from '../../../../models';

const MIN_SEARCH_LENGTH = 3;
const SEARCH_TYPE = 'ONLY_HOTEL';

@Component({
  selector: 'app-tab-hotel',
  templateUrl: './tab-hotel.component.html',
  styleUrls: ['./tab-hotel.component.scss'],
})
export class TabHotelComponent implements OnInit, OnDestroy {
  @ViewChild(AutocompleteDropdownComponent) autocomplete!: AutocompleteDropdownComponent;

  @ViewChild('popUp') popUpElement: PopUpPasajeroComponent | undefined;
  @ViewChild('childDates') childDates!: InputRangeComponent;

  private readonly destroy$ = new Subject<void>();

  private destinations: any[] = [];
  destinationSuggestions: AutoCompleteItem[] = [];
  destinationLoading = false;

  form: FormGroup = this.fb.group({
    destino: ['', [Validators.required, Validators.minLength(3)]],
  });

  fromDate: NgbDate | null;
  toDate: NgbDate | null;

  distribution = '';
  distributionObject: DistributionObjectA;

  isSubmit = false;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    private readonly destinationService: DestinyService,
    private readonly notificationService: NotificationService,
    private readonly fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.listenToInputChanges();

    this.destinationService.destinationHotel$
      .pipe(
        takeUntil(this.destroy$),
        filter((data) => !!data),
        tap((data) => {
          this.destination.setValue(data.id);
          this.fetchSuggestions(data.id, false);
        })
      )
      .subscribe();
  }

  private listenToInputChanges() {
    this.destination.valueChanges
      .pipe(
        map((search: string) => search?.toLowerCase().trim() ?? ''),
        tap((search) => {
          if (search.length < MIN_SEARCH_LENGTH) {
            this.destinations = [];
            this.destinationSuggestions = [];
          }
        }),
        filter((search) => search.length >= MIN_SEARCH_LENGTH),
        debounceTime(400),
        tap((search) => this.fetchSuggestions(search)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private fetchSuggestions(query: string, displaySuggestions = true) {
    this.destinationLoading = displaySuggestions;

    this.destinationService
      .getDestinyPaqueteDinamico(query, SEARCH_TYPE)
      .pipe(
        take(1),
        map((data) => ({
          raw: data,
          mapped: this.mapToAutoCompleteItems(data),
        })),
        finalize(() => (this.destinationLoading = false))
      )
      .subscribe({
        next: ({ raw, mapped }) => {
          this.destinations = raw;
          if (displaySuggestions) {
            this.destinationSuggestions = mapped;
            this.autocomplete?.showSuggestions();
          }
        },
      });
  }

  private mapToAutoCompleteItems(data: any[]) {
    return data.map(
      (d): AutoCompleteItem => ({
        id: d.id,
        codigo: d.value,
        country: d.country,
        title: d.label,
        children: [],
        isSelected: false,
        text: d.label,
      })
    );
  }

  private getErrorsForm(form: FormGroup) {
    const errors: string[] = [];

    if (form.controls['destino'].invalid) errors.push('El campo destino es obligatorio');
    if (!this.toDate) errors.push('La fecha final es requerido');
    if (!this.fromDate) errors.push('La fecha de inicio es requerido');

    return errors;
  }

  private navigateToResponseUrl(url: string) {
    if (isPlatformBrowser(this.platformId)) window.open(url, '_blank');
  }

  public async searchAlojamiento() {
    this.isSubmit = true;
    const valuesDateRange = this.childDates.getValuesByHotel();
    this.toDate = valuesDateRange.arrivalDate;
    this.fromDate = valuesDateRange.departureDate;

    const errosInputs = this.getErrorsForm(this.form);
    if (errosInputs.length > 0) {
      this.notificationService.showNotificacion('Error', errosInputs.join(', '), 10);
      return;
    }

    const errorHabitaciones = this.popUpElement?.isValid();
    if (!errorHabitaciones?.isValid) {
      this.notificationService.showNotificacion(
        'Error',
        errorHabitaciones?.message || 'Error en las habitaciones'
      );
      return;
    }

    const url = this.getUrlAlojamiento();
    this.navigateToResponseUrl(url);
  }

  private getUrlAlojamiento() {
    const params = this.getParamsAlojamiento();
    return new URLHotel(params, this.distribution).getUrl();
  }

  private getParamsAlojamiento() {
    return new ParamsHoteles(this.fromDate, this.toDate, this.form, this.destinations).getParams();
  }

  onDestinationSelect(selected: AutoCompleteItem) {
    this.destination.setValue(selected.title, { emitEvent: false });
    this.destinationSuggestions = [];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get destination() {
    return this.form.get('destino') as FormControl<string>;
  }
}
